import { supabase } from "@/lib/supabase";

// ============================================================
// Row conversion helpers (snake_case <-> camelCase)
// ============================================================

const productFromRow = (r) => r && ({
  id: r.id,
  name: r.name,
  category: r.category,
  unit: r.unit,
  unitCost: Number(r.unit_cost),
  defaultPrice:
    r.default_price != null
      ? Number(r.default_price)
      : null,
  isActive: r.is_active,
  remark: r.remark,
  createdAt: r.created_at,
  updatedAt: r.updated_at,
});

const productToRow = (p) => ({
  name: p.name,
  category: p.category,
  unit: p.unit,
  unit_cost: p.unitCost,
  default_price:
    p.defaultPrice ?? null,
  is_active:
    p.isActive ?? true,
  remark:
    p.remark ?? null,
});

const eventFromRow = (
  r,
  products = [],
  staff = []
) =>
  r && ({
    id: r.id,
    eventName: r.event_name,
    activityTypeId:
      r.activity_type_id ||
      null,
    status:
      r.status ||
      "completed",
    startDate: r.start_date,
    endDate: r.end_date,
    totalEventDays:
      r.total_event_days,
    generalNotes:
      r.general_notes,

    revenueType:
      r.revenue_type ||
      "product_sales",

    productSalesCollected:
      Number(
        r.product_sales_collected ??
          r.total_sales_collected ??
          0
      ),

    otherRevenueAmount:
      Number(
        r.other_revenue_amount ??
          0
      ),

    otherRevenueRemark:
      r.other_revenue_remark,

    products:
      products.map(
        eventProductFromRow
      ),

    staffWages:
      staff.map(
        staffFromRow
      ),

    rentalCost:
      Number(
        r.rental_cost || 0
      ),

    transportCost:
      Number(
        r.transport_cost || 0
      ),

    otherCost:
      Number(
        r.other_cost || 0
      ),

    otherCostRemark:
      r.other_cost_remark,

    totalSalesCollected:
      Number(
        r.total_sales_collected ||
          0
      ),

    totalRecordedProductSales:
      Number(
        r.total_recorded_product_sales ||
          0
      ),

    unrecordedSalesAmount:
      Number(
        r.unrecorded_sales_amount ||
          0
      ),

    rawUnrecordedCupEquivalent:
      Number(
        r.raw_unrecorded_cup_equivalent ||
          0
      ),

    estimatedUnrecordedWholeCups:
      r.estimated_unrecorded_whole_cups ||
      0,

    estimatedUnrecordedCogs:
      Number(
        r.estimated_unrecorded_cogs ||
          0
      ),

    standardCupSellingPrice:
      Number(
        r.standard_cup_selling_price ||
          8.9
      ),

    conservativeCupUnitCost:
      Number(
        r.conservative_cup_unit_cost ||
          1.38
      ),

    roundingMethod:
      r.rounding_method,

    reconciliationStatus:
      r.reconciliation_status,

    totalCostOfGoodsSold:
      Number(
        r.total_cost_of_goods_sold ||
          0
      ),

    totalSales:
      Number(
        r.total_sales || 0
      ),

    totalProductCost:
      Number(
        r.total_product_cost ||
          0
      ),

    totalProductGrossProfit:
      Number(
        r.total_product_gross_profit ||
          0
      ),

    totalStaffWages:
      Number(
        r.total_staff_wages ||
          0
      ),

    totalCost:
      Number(
        r.total_cost || 0
      ),

    netProfit:
      Number(
        r.net_profit || 0
      ),

    profitMargin:
      Number(
        r.profit_margin || 0
      ),

    totalProductQuantitySold:
      Number(
        r.total_product_quantity_sold ||
          0
      ),

    averageSalesPerDay:
      Number(
        r.average_sales_per_day ||
          0
      ),

    averageNetProfitPerDay:
      Number(
        r.average_net_profit_per_day ||
          0
      ),

    createdAt:
      r.created_at,

    updatedAt:
      r.updated_at,
  });

const eventProductFromRow = (r) => ({
  id: r.id,
  productId:
    r.original_product_id,
  productName:
    r.product_name,
  category:
    r.category,
  unit:
    r.unit,
  unitCost:
    Number(
      r.unit_cost
    ),
  defaultPrice:
    r.default_price != null
      ? Number(
          r.default_price
        )
      : null,
  quantitySold:
    Number(
      r.quantity_sold
    ),
  totalSalesAmount:
    Number(
      r.total_sales_amount
    ),
  productCost:
    Number(
      r.product_cost
    ),
  productGrossProfit:
    Number(
      r.product_gross_profit
    ),
  remark:
    r.remark,
});

const staffFromRow = (r) => ({
  id: r.id,
  staffName:
    r.staff_name,
  wageAmount:
    Number(
      r.wage_amount
    ),
  remark:
    r.remark,
});

const eventProductToRow = (
  eventId,
  p
) => ({
  event_id:
    eventId,
  original_product_id:
    p.productId,
  product_name:
    p.productName,
  category:
    p.category,
  unit:
    p.unit,
  unit_cost:
    p.unitCost,
  default_price:
    p.defaultPrice ??
    null,
  quantity_sold:
    p.quantitySold,
  total_sales_amount:
    p.totalSalesAmount,
  product_cost:
    p.productCost,
  product_gross_profit:
    p.productGrossProfit,
  remark:
    p.remark ??
    null,
});

const staffToRow = (
  eventId,
  s
) => ({
  event_id:
    eventId,
  staff_name:
    s.staffName,
  wage_amount:
    s.wageAmount,
  remark:
    s.remark ??
    null,
});

const eventToRow = (e) => ({
  event_name:
    e.eventName,

  activity_type_id:
    e.activityTypeId ||
    null,

  status:
    e.status ||
    "completed",

  start_date:
    e.startDate,

  end_date:
    e.endDate,

  total_event_days:
    e.totalEventDays,

  general_notes:
    e.generalNotes ??
    null,

  revenue_type:
    e.revenueType ||
    "product_sales",

  product_sales_collected:
    e.productSalesCollected ??
    e.totalSalesCollected ??
    0,

  other_revenue_amount:
    e.otherRevenueAmount ??
    0,

  other_revenue_remark:
    e.otherRevenueRemark ??
    null,

  rental_cost:
    e.rentalCost ||
    0,

  transport_cost:
    e.transportCost ||
    0,

  other_cost:
    e.otherCost ||
    0,

  other_cost_remark:
    e.otherCostRemark ??
    null,

  total_sales_collected:
    e.totalSalesCollected ??
    (
      Number(
        e.productSalesCollected ||
          0
      ) +
      Number(
        e.otherRevenueAmount ||
          0
      )
    ),

  total_recorded_product_sales:
    e.totalRecordedProductSales ||
    0,

  unrecorded_sales_amount:
    e.unrecordedSalesAmount ||
    0,

  raw_unrecorded_cup_equivalent:
    e.rawUnrecordedCupEquivalent ||
    0,

  estimated_unrecorded_whole_cups:
    e.estimatedUnrecordedWholeCups ||
    0,

  estimated_unrecorded_cogs:
    e.estimatedUnrecordedCogs ||
    0,

  standard_cup_selling_price:
    e.standardCupSellingPrice ||
    8.9,

  conservative_cup_unit_cost:
    e.conservativeCupUnitCost ||
    1.38,

  rounding_method:
    e.roundingMethod ||
    "Rounded upward to the next whole cup",

  reconciliation_status:
    e.reconciliationStatus ||
    "Manual Review Required",

  total_cost_of_goods_sold:
    e.totalCostOfGoodsSold ||
    0,

  total_sales:
    e.totalSales ||
    0,

  total_product_cost:
    e.totalProductCost ||
    0,

  total_product_gross_profit:
    e.totalProductGrossProfit ||
    0,

  total_staff_wages:
    e.totalStaffWages ||
    0,

  total_cost:
    e.totalCost ||
    0,

  net_profit:
    e.netProfit ||
    0,

  profit_margin:
    e.profitMargin ||
    0,

  total_product_quantity_sold:
    e.totalProductQuantitySold ||
    0,

  average_sales_per_day:
    e.averageSalesPerDay ||
    0,

  average_net_profit_per_day:
    e.averageNetProfitPerDay ||
    0,
});

// ============================================================
// Products
// ============================================================

export async function listProducts() {
  const {
    data,
    error,
  } =
    await supabase
      .from("products")
      .select("*")
      .order(
        "created_at",
        {
          ascending:
            false,
        }
      );

  if (error) {
    throw error;
  }

  return (
    data || []
  ).map(
    productFromRow
  );
}

export async function createProduct(
  input
) {
  const {
    data,
    error,
  } =
    await supabase
      .from("products")
      .insert(
        productToRow(
          input
        )
      )
      .select()
      .single();

  if (error) {
    throw error;
  }

  return productFromRow(
    data
  );
}

export async function updateProduct(
  id,
  input
) {
  const patch = {
    ...productToRow(
      input
    ),
  };

  const {
    data,
    error,
  } =
    await supabase
      .from("products")
      .update(patch)
      .eq(
        "id",
        id
      )
      .select()
      .single();

  if (error) {
    throw error;
  }

  return productFromRow(
    data
  );
}

export async function toggleProductStatus(
  id
) {
  const {
    data:
      current,
    error:
      e1,
  } =
    await supabase
      .from("products")
      .select(
        "is_active"
      )
      .eq(
        "id",
        id
      )
      .single();

  if (e1) {
    throw e1;
  }

  const {
    data,
    error,
  } =
    await supabase
      .from("products")
      .update({
        is_active:
          !current.is_active,
      })
      .eq(
        "id",
        id
      )
      .select()
      .single();

  if (error) {
    throw error;
  }

  return {
    success:
      true,
    isActive:
      data.is_active,
  };
}
// ============================================================
// Categories
// ============================================================

export async function listCategories() {
  const {
    data,
    error,
  } =
    await supabase
      .from(
        "product_categories"
      )
      .select("*")
      .order(
        "name",
        {
          ascending:
            true,
        }
      );

  if (error) {
    throw error;
  }

  return (
    data || []
  ).map(
    (r) => ({
      id:
        r.id,
      name:
        r.name,
      isDefault:
        r.is_default,
      createdAt:
        r.created_at,
    })
  );
}

export async function createCategory(
  name
) {
  const {
    data,
    error,
  } =
    await supabase
      .from(
        "product_categories"
      )
      .insert({
        name,
        is_default:
          false,
      })
      .select()
      .single();

  if (error) {
    throw error;
  }

  return {
    id:
      data.id,
    name:
      data.name,
    isDefault:
      data.is_default,
    createdAt:
      data.created_at,
  };
}

// ============================================================
// Units
// ============================================================

export async function listUnits() {
  const {
    data,
    error,
  } =
    await supabase
      .from(
        "product_units"
      )
      .select("*")
      .order(
        "name",
        {
          ascending:
            true,
        }
      );

  if (error) {
    throw error;
  }

  return (
    data || []
  ).map(
    (r) => ({
      id:
        r.id,
      name:
        r.name,
      isDefault:
        r.is_default,
      createdAt:
        r.created_at,
    })
  );
}

export async function createUnit(
  name
) {
  const {
    data,
    error,
  } =
    await supabase
      .from(
        "product_units"
      )
      .insert({
        name,
        is_default:
          false,
      })
      .select()
      .single();

  if (error) {
    throw error;
  }

  return {
    id:
      data.id,
    name:
      data.name,
    isDefault:
      data.is_default,
    createdAt:
      data.created_at,
  };
}

// ============================================================
// Reconciliation Settings
// ============================================================

export async function getReconciliationSettings() {
  const {
    data,
    error,
  } =
    await supabase
      .from(
        "reconciliation_settings"
      )
      .select("*")
      .eq(
        "id",
        "singleton"
      )
      .maybeSingle();

  if (error) {
    throw error;
  }

  if (!data) {
    return {
      standardCupSellingPrice:
        8.9,

      conservativeCupUnitCost:
        1.38,

      remark:
        "The conservative unit cost uses the highest current Cup Herbal Tea unit cost.",
    };
  }

  return {
    standardCupSellingPrice:
      Number(
        data.standard_cup_selling_price
      ),

    conservativeCupUnitCost:
      Number(
        data.conservative_cup_unit_cost
      ),

    remark:
      data.remark,
  };
}

export async function updateReconciliationSettings(
  settings
) {
  const row = {
    id:
      "singleton",

    standard_cup_selling_price:
      settings.standardCupSellingPrice,

    conservative_cup_unit_cost:
      settings.conservativeCupUnitCost,

    remark:
      settings.remark,
  };

  const {
    error,
  } =
    await supabase
      .from(
        "reconciliation_settings"
      )
      .upsert(
        row
      );

  if (error) {
    throw error;
  }

  return settings;
}

// ============================================================
// Activity Types
// ============================================================

const activityTypeFromRow = (
  r
) =>
  r && ({
    id:
      r.id,
    name:
      r.name,
    isDefault:
      r.is_default,
    isArchived:
      r.is_archived,
    sortOrder:
      r.sort_order,
  });

export async function listActivityTypes({
  includeArchived =
    false,
} = {}) {
  let q =
    supabase
      .from(
        "activity_types"
      )
      .select("*")
      .order(
        "sort_order",
        {
          ascending:
            true,
        }
      );

  if (
    !includeArchived
  ) {
    q =
      q.eq(
        "is_archived",
        false
      );
  }

  const {
    data,
    error,
  } = await q;

  if (error) {
    throw error;
  }

  return (
    data || []
  ).map(
    activityTypeFromRow
  );
}

export async function createActivityType(
  name
) {
  const {
    data:
      sess,
  } =
    await supabase.auth.getSession();

  const uid =
    sess?.session?.user?.id;

  if (!uid) {
    throw new Error(
      "Not authenticated"
    );
  }

  const {
    data,
    error,
  } =
    await supabase
      .from(
        "activity_types"
      )
      .insert({
        user_id:
          uid,
        name,
        is_default:
          false,
        is_archived:
          false,
        sort_order:
          999,
      })
      .select()
      .single();

  if (error) {
    throw error;
  }

  return activityTypeFromRow(
    data
  );
}

export async function updateActivityType(
  id,
  patch
) {
  const row = {};

  if (
    patch.name !==
    undefined
  ) {
    row.name =
      patch.name;
  }

  if (
    patch.isArchived !==
    undefined
  ) {
    row.is_archived =
      patch.isArchived;
  }

  const {
    data,
    error,
  } =
    await supabase
      .from(
        "activity_types"
      )
      .update(row)
      .eq(
        "id",
        id
      )
      .select()
      .single();

  if (error) {
    throw error;
  }

  return activityTypeFromRow(
    data
  );
}

// ============================================================
// Transaction Types
// ============================================================

const transactionTypeFromRow = (
  r
) =>
  r && ({
    id:
      r.id,
    name:
      r.name,
    plTreatment:
      r.pl_treatment,
    isDefault:
      r.is_default,
    isArchived:
      r.is_archived,
    sortOrder:
      r.sort_order,
  });

export async function listTransactionTypes({
  includeArchived =
    false,
} = {}) {
  let q =
    supabase
      .from(
        "transaction_types"
      )
      .select("*")
      .order(
        "sort_order",
        {
          ascending:
            true,
        }
      );

  if (
    !includeArchived
  ) {
    q =
      q.eq(
        "is_archived",
        false
      );
  }

  const {
    data,
    error,
  } = await q;

  if (error) {
    throw error;
  }

  return (
    data || []
  ).map(
    transactionTypeFromRow
  );
}

export async function createTransactionType({
  name,
  plTreatment,
}) {
  const {
    data:
      sess,
  } =
    await supabase.auth.getSession();

  const uid =
    sess?.session?.user?.id;

  if (!uid) {
    throw new Error(
      "Not authenticated"
    );
  }

  const {
    data,
    error,
  } =
    await supabase
      .from(
        "transaction_types"
      )
      .insert({
        user_id:
          uid,
        name,
        pl_treatment:
          plTreatment,
        is_default:
          false,
        is_archived:
          false,
        sort_order:
          999,
      })
      .select()
      .single();

  if (error) {
    throw error;
  }

  return transactionTypeFromRow(
    data
  );
}

export async function updateTransactionType(
  id,
  patch
) {
  const row = {};

  if (
    patch.name !==
    undefined
  ) {
    row.name =
      patch.name;
  }

  if (
    patch.plTreatment !==
    undefined
  ) {
    row.pl_treatment =
      patch.plTreatment;
  }

  if (
    patch.isArchived !==
    undefined
  ) {
    row.is_archived =
      patch.isArchived;
  }

  const {
    data,
    error,
  } =
    await supabase
      .from(
        "transaction_types"
      )
      .update(row)
      .eq(
        "id",
        id
      )
      .select()
      .single();

  if (error) {
    throw error;
  }

  return transactionTypeFromRow(
    data
  );
}

// ============================================================
// Transaction Categories
// ============================================================

const transactionCategoryFromRow = (
  r
) =>
  r && ({
    id:
      r.id,
    name:
      r.name,
    transactionTypeId:
      r.transaction_type_id,
    isDefault:
      r.is_default,
    isArchived:
      r.is_archived,
    sortOrder:
      r.sort_order,
  });

export async function listTransactionCategories({
  includeArchived =
    false,
  transactionTypeId =
    null,
} = {}) {
  let q =
    supabase
      .from(
        "transaction_categories"
      )
      .select("*")
      .order(
        "sort_order",
        {
          ascending:
            true,
        }
      );

  if (
    !includeArchived
  ) {
    q =
      q.eq(
        "is_archived",
        false
      );
  }

  if (
    transactionTypeId
  ) {
    q =
      q.eq(
        "transaction_type_id",
        transactionTypeId
      );
  }

  const {
    data,
    error,
  } = await q;

  if (error) {
    throw error;
  }

  return (
    data || []
  ).map(
    transactionCategoryFromRow
  );
}

export async function createTransactionCategory({
  name,
  transactionTypeId,
}) {
  const {
    data:
      sess,
  } =
    await supabase.auth.getSession();

  const uid =
    sess?.session?.user?.id;

  if (!uid) {
    throw new Error(
      "Not authenticated"
    );
  }

  const {
    data,
    error,
  } =
    await supabase
      .from(
        "transaction_categories"
      )
      .insert({
        user_id:
          uid,
        name,
        transaction_type_id:
          transactionTypeId,
        is_default:
          false,
        is_archived:
          false,
        sort_order:
          999,
      })
      .select()
      .single();

  if (error) {
    throw error;
  }

  return transactionCategoryFromRow(
    data
  );
}

export async function updateTransactionCategory(
  id,
  patch
) {
  const row = {};

  if (
    patch.name !==
    undefined
  ) {
    row.name =
      patch.name;
  }

  if (
    patch.isArchived !==
    undefined
  ) {
    row.is_archived =
      patch.isArchived;
  }

  const {
    data,
    error,
  } =
    await supabase
      .from(
        "transaction_categories"
      )
      .update(row)
      .eq(
        "id",
        id
      )
      .select()
      .single();

  if (error) {
    throw error;
  }

  return transactionCategoryFromRow(
    data
  );
}

// ============================================================
// Tax Treatment Statuses
// ============================================================

const taxTreatmentFromRow = (
  r
) =>
  r && ({
    id:
      r.id,
    name:
      r.name,
    isDefault:
      r.is_default,
    isArchived:
      r.is_archived,
    sortOrder:
      r.sort_order,
  });

export async function listTaxTreatmentStatuses({
  includeArchived =
    false,
} = {}) {
  let q =
    supabase
      .from(
        "tax_treatment_statuses"
      )
      .select("*")
      .order(
        "sort_order",
        {
          ascending:
            true,
        }
      );

  if (
    !includeArchived
  ) {
    q =
      q.eq(
        "is_archived",
        false
      );
  }

  const {
    data,
    error,
  } = await q;

  if (error) {
    throw error;
  }

  return (
    data || []
  ).map(
    taxTreatmentFromRow
  );
}

export async function createTaxTreatmentStatus(
  name
) {
  const {
    data:
      sess,
  } =
    await supabase.auth.getSession();

  const uid =
    sess?.session?.user?.id;

  if (!uid) {
    throw new Error(
      "Not authenticated"
    );
  }

  const {
    data,
    error,
  } =
    await supabase
      .from(
        "tax_treatment_statuses"
      )
      .insert({
        user_id:
          uid,
        name,
        is_default:
          false,
        is_archived:
          false,
        sort_order:
          999,
      })
      .select()
      .single();

  if (error) {
    throw error;
  }

  return taxTreatmentFromRow(
    data
  );
}

// ============================================================
// Transactions
// ============================================================

const transactionFromRow = (
  r
) =>
  r && ({
    id:
      r.id,

    txDate:
      r.tx_date,

    transactionTypeId:
      r.transaction_type_id,

    categoryId:
      r.category_id,

    taxTreatmentId:
      r.tax_treatment_id,

    supplierCustomer:
      r.supplier_customer,

    description:
      r.description,

    amount:
      Number(
        r.amount || 0
      ),

    paymentMethod:
      r.payment_method,

    receiptNumber:
      r.receipt_number,

    receiptStatus:
      r.receipt_status,

    relatedEventId:
      r.related_event_id,

    businessUsePercentage:
      Number(
        r.business_use_percentage ??
          100
      ),

    quantity:
      r.quantity != null
        ? Number(
            r.quantity
          )
        : null,

    unitCost:
      r.unit_cost != null
        ? Number(
            r.unit_cost
          )
        : null,

    cashFlowDirection:
      r.cash_flow_direction,

    notes:
      r.notes,

    createdAt:
      r.created_at,

    updatedAt:
      r.updated_at,
  });

const transactionToRow = (
  t
) => ({
  tx_date:
    t.txDate,

  transaction_type_id:
    t.transactionTypeId,

  category_id:
    t.categoryId,

  tax_treatment_id:
    t.taxTreatmentId ||
    null,

  supplier_customer:
    t.supplierCustomer ||
    null,

  description:
    t.description ||
    null,

  amount:
    Number(
      t.amount || 0
    ),

  payment_method:
    t.paymentMethod ||
    null,

  receipt_number:
    t.receiptNumber ||
    null,

  receipt_status:
    t.receiptStatus ||
    "Missing",

  related_event_id:
    t.relatedEventId ||
    null,

  business_use_percentage:
    t.businessUsePercentage ??
    100,

  quantity:
    t.quantity != null &&
    t.quantity !== ""
      ? Number(
          t.quantity
        )
      : null,

  unit_cost:
    t.unitCost != null &&
    t.unitCost !== ""
      ? Number(
          t.unitCost
        )
      : null,

  cash_flow_direction:
    t.cashFlowDirection ||
    null,

  notes:
    t.notes ||
    null,
});

export async function listTransactions({
  from =
    null,

  to =
    null,

  transactionTypeId =
    "all",

  categoryId =
    "all",

  taxTreatmentId =
    "all",

  relatedEventId =
    null,

  search =
    "",

  sortBy =
    "date_desc",

  limit =
    null,
} = {}) {
  let q =
    supabase
      .from(
        "transactions"
      )
      .select("*");

  if (from) {
    q =
      q.gte(
        "tx_date",
        from
      );
  }

  if (to) {
    q =
      q.lte(
        "tx_date",
        to
      );
  }

  if (
    transactionTypeId &&
    transactionTypeId !==
      "all"
  ) {
    q =
      q.eq(
        "transaction_type_id",
        transactionTypeId
      );
  }

  if (
    categoryId &&
    categoryId !==
      "all"
  ) {
    q =
      q.eq(
        "category_id",
        categoryId
      );
  }

  if (
    taxTreatmentId &&
    taxTreatmentId !==
      "all"
  ) {
    q =
      q.eq(
        "tax_treatment_id",
        taxTreatmentId
      );
  }

  if (
    relatedEventId
  ) {
    q =
      q.eq(
        "related_event_id",
        relatedEventId
      );
  }

  const sortMap = {
    date_desc: {
      col:
        "tx_date",
      asc:
        false,
    },

    date_asc: {
      col:
        "tx_date",
      asc:
        true,
    },

    amount_desc: {
      col:
        "amount",
      asc:
        false,
    },

    amount_asc: {
      col:
        "amount",
      asc:
        true,
    },
  };

  const s =
    sortMap[
      sortBy
    ] ||
    sortMap.date_desc;

  q =
    q.order(
      s.col,
      {
        ascending:
          s.asc,
      }
    );

  if (limit) {
    q =
      q.limit(
        limit
      );
  }

  const {
    data,
    error,
  } = await q;

  if (error) {
    throw error;
  }

  let rows =
    (
      data || []
    ).map(
      transactionFromRow
    );

  const term =
    (
      search ||
      ""
    )
      .trim()
      .toLowerCase();

  if (term) {
    rows =
      rows.filter(
        (r) => {
          const hay =
            [
              r.description,
              r.supplierCustomer,
              r.receiptNumber,
              r.notes,
            ]
              .filter(
                Boolean
              )
              .join(
                " "
              )
              .toLowerCase();

          return hay.includes(
            term
          );
        }
      );
  }

  return rows;
}

export async function createTransaction(
  t
) {
  const {
    data:
      sess,
  } =
    await supabase.auth.getSession();

  const uid =
    sess?.session?.user?.id;

  if (!uid) {
    throw new Error(
      "Not authenticated"
    );
  }

  const {
    data,
    error,
  } =
    await supabase
      .from(
        "transactions"
      )
      .insert({
        ...transactionToRow(
          t
        ),
        user_id:
          uid,
      })
        .select()
      .single();

  if (error) {
    throw error;
  }

  return transactionFromRow(
    data
  );
}

export async function updateTransaction(
  id,
  t
) {
  const {
    data,
    error,
  } =
    await supabase
      .from(
        "transactions"
      )
      .update(
        transactionToRow(
          t
        )
      )
      .eq(
        "id",
        id
      )
      .select()
      .single();

  if (error) {
    throw error;
  }

  return transactionFromRow(
    data
  );
}

export async function deleteTransaction(
  id
) {
  const {
    error,
  } =
    await supabase
      .from(
        "transactions"
      )
      .delete()
      .eq(
        "id",
        id
      );

  if (error) {
    throw error;
  }

  return {
    success:
      true,
  };
}

/*
 * ============================================================
 * LINKED PREPAID EVENT RENTAL
 * ============================================================
 *
 * A Prepaid Event Rental transaction:
 *
 * - is paid on its real Transaction Date
 * - is linked to one exact event through related_event_id
 * - does NOT become a normal operating expense in the payment month
 * - will later be included in that linked Event's Rental Cost
 *
 * IMPORTANT:
 *
 * These helpers only READ and TOTAL the linked transactions.
 *
 * They do not modify the events table.
 * They do not modify Event P&L calculations yet.
 * They do not change Monthly P&L yet.
 *
 * This keeps the implementation safe while the Event form and
 * Event P&L are updated in the next steps.
 * ============================================================
 */

export async function listLinkedPrepaidEventRentals(
  eventId
) {
  if (!eventId) {
    return [];
  }

  /*
   * Find every Transaction Type currently classified
   * as prepaid_event_rental.
   *
   * We deliberately search by pl_treatment rather than
   * hard-coding a Transaction Type UUID.
   *
   * This means the logic will continue to work even if
   * the Transaction Type record is recreated later.
   */
  const {
    data:
      typeRows,
    error:
      typeError,
  } =
    await supabase
      .from(
        "transaction_types"
      )
      .select(
        "id, pl_treatment"
      )
      .eq(
        "pl_treatment",
        "prepaid_event_rental"
      );

  if (typeError) {
    throw typeError;
  }

  const typeIds =
    (
      typeRows ||
      []
    )
      .map(
        (r) =>
          r.id
      )
      .filter(
        Boolean
      );

  if (
    typeIds.length ===
    0
  ) {
    return [];
  }

  /*
   * Fetch only transactions linked to this exact event
   * AND using the prepaid event rental treatment.
   */
  const {
    data,
    error,
  } =
    await supabase
      .from(
        "transactions"
      )
      .select("*")
      .eq(
        "related_event_id",
        eventId
      )
      .in(
        "transaction_type_id",
        typeIds
      )
      .order(
        "tx_date",
        {
          ascending:
            true,
        }
      );

  if (error) {
    throw error;
  }

  return (
    data || []
  ).map(
    transactionFromRow
  );
}

export async function getLinkedPrepaidEventRentalTotal(
  eventId
) {
  const rows =
    await listLinkedPrepaidEventRentals(
      eventId
    );

  return rows.reduce(
    (
      total,
      transaction
    ) =>
      total +
      Number(
        transaction.amount ||
          0
      ),
    0
  );
}

export async function getLinkedPrepaidEventRentalSummary(
  eventId
) {
  const transactions =
    await listLinkedPrepaidEventRentals(
      eventId
    );

  const total =
    transactions.reduce(
      (
        sum,
        transaction
      ) =>
        sum +
        Number(
          transaction.amount ||
            0
        ),
      0
    );

  return {
    eventId,
    total,
    count:
      transactions.length,
    transactions,
  };
}

// ============================================================
// Asset Statuses
// ============================================================

const assetStatusFromRow = (
  r
) =>
  r && ({
    id:
      r.id,
    name:
      r.name,
    isDefault:
      r.is_default,
    isArchived:
      r.is_archived,
    sortOrder:
      r.sort_order,
  });

export async function listAssetStatuses({
  includeArchived =
    false,
} = {}) {
  let q =
    supabase
      .from(
        "asset_statuses"
      )
      .select("*")
      .order(
        "sort_order",
        {
          ascending:
            true,
        }
      );

  if (
    !includeArchived
  ) {
    q =
      q.eq(
        "is_archived",
        false
      );
  }

  const {
    data,
    error,
  } = await q;

  if (error) {
    throw error;
  }

  return (
    data || []
  ).map(
    assetStatusFromRow
  );
}

// ============================================================
// Assets
// ============================================================

const assetFromRow = (
  r
) =>
  r && ({
    id:
      r.id,
    purchaseDate:
      r.purchase_date,
    name:
      r.name,
    supplier:
      r.supplier,
    category:
      r.category,
    purchaseAmount:
      Number(
        r.purchase_amount ||
          0
      ),
    businessUsePercentage:
      Number(
        r.business_use_percentage ??
          100
      ),
    receiptNumber:
      r.receipt_number,
    assetStatusId:
      r.asset_status_id,
    disposalDate:
      r.disposal_date,
    taxCapitalAllowanceStatus:
      r.tax_capital_allowance_status,
    linkedTransactionId:
      r.linked_transaction_id,
    notes:
      r.notes,
    createdAt:
      r.created_at,
    updatedAt:
      r.updated_at,
  });

const assetToRow = (
  a
) => ({
  purchase_date:
    a.purchaseDate,

  name:
    a.name,

  supplier:
    a.supplier ||
    null,

  category:
    a.category ||
    null,

  purchase_amount:
    Number(
      a.purchaseAmount ||
        0
    ),

  business_use_percentage:
    a.businessUsePercentage ??
    100,

  receipt_number:
    a.receiptNumber ||
    null,

  asset_status_id:
    a.assetStatusId ||
    null,

  disposal_date:
    a.disposalDate ||
    null,

  tax_capital_allowance_status:
    a.taxCapitalAllowanceStatus ||
    null,

  linked_transaction_id:
    a.linkedTransactionId ||
    null,

  notes:
    a.notes ||
    null,
});

export async function listAssets({
  statusId =
    "all",

  search =
    "",

  sortBy =
    "date_desc",
} = {}) {
  let q =
    supabase
      .from(
        "assets"
      )
      .select("*");

  if (
    statusId &&
    statusId !==
      "all"
  ) {
    q =
      q.eq(
        "asset_status_id",
        statusId
      );
  }

  const sortMap = {
    date_desc: {
      col:
        "purchase_date",
      asc:
        false,
    },

    date_asc: {
      col:
        "purchase_date",
      asc:
        true,
    },

    amount_desc: {
      col:
        "purchase_amount",
      asc:
        false,
    },

    amount_asc: {
      col:
        "purchase_amount",
      asc:
        true,
    },

    name: {
      col:
        "name",
      asc:
        true,
    },
  };

  const s =
    sortMap[
      sortBy
    ] ||
    sortMap.date_desc;

  q =
    q.order(
      s.col,
      {
        ascending:
          s.asc,
      }
    );

  const {
    data,
    error,
  } = await q;

  if (error) {
    throw error;
  }

  let rows =
    (
      data || []
    ).map(
      assetFromRow
    );

  const term =
    (
      search ||
      ""
    )
      .trim()
      .toLowerCase();

  if (term) {
    rows =
      rows.filter(
        (r) =>
          [
            r.name,
            r.supplier,
            r.category,
            r.notes,
          ]
            .filter(
              Boolean
            )
            .join(
              " "
            )
            .toLowerCase()
            .includes(
              term
            )
      );
  }

  return rows;
}

export async function createAsset(
  a
) {
  const {
    data:
      sess,
  } =
    await supabase.auth.getSession();

  const uid =
    sess?.session?.user?.id;

  if (!uid) {
    throw new Error(
      "Not authenticated"
    );
  }

  const {
    data,
    error,
  } =
    await supabase
      .from(
        "assets"
      )
      .insert({
        ...assetToRow(
          a
        ),
        user_id:
          uid,
      })
      .select()
      .single();

  if (error) {
    throw error;
  }

  return assetFromRow(
    data
  );
}

export async function updateAsset(
  id,
  a
) {
  const {
    data,
    error,
  } =
    await supabase
      .from(
        "assets"
      )
      .update(
        assetToRow(
          a
        )
      )
      .eq(
        "id",
        id
      )
      .select()
      .single();

  if (error) {
    throw error;
  }

  return assetFromRow(
    data
  );
}

export async function deleteAsset(
  id
) {
  const {
    error,
  } =
    await supabase
      .from(
        "assets"
      )
      .delete()
      .eq(
        "id",
        id
      );

  if (error) {
    throw error;
  }

  return {
    success:
      true,
  };
}

// ============================================================
// Tax Adjustment Types + Tax Adjustments
// ============================================================

const taxAdjustmentTypeFromRow = (
  r
) =>
  r && ({
    id:
      r.id,
    name:
      r.name,
    isDefault:
      r.is_default,
    isArchived:
      r.is_archived,
    sortOrder:
      r.sort_order,
  });

export async function listTaxAdjustmentTypes({
  includeArchived =
    false,
} = {}) {
  let q =
    supabase
      .from(
        "tax_adjustment_types"
      )
      .select("*")
      .order(
        "sort_order",
        {
          ascending:
            true,
        }
      );

  if (
    !includeArchived
  ) {
    q =
      q.eq(
        "is_archived",
        false
      );
  }

  const {
    data,
    error,
  } = await q;

  if (error) {
    throw error;
  }

  return (
    data || []
  ).map(
    taxAdjustmentTypeFromRow
  );
}

const taxAdjustmentFromRow = (
  r
) =>
  r && ({
    id:
      r.id,

    assessmentYear:
      r.assessment_year,

    adjustmentTypeId:
      r.adjustment_type_id,

    adjustmentDate:
      r.adjustment_date,

    description:
      r.description,

    amount:
      Number(
        r.amount || 0
      ),

    notes:
      r.notes,

    supportingDocumentUrl:
      r.supporting_document_url,

    createdAt:
      r.created_at,
  });

const taxAdjustmentToRow = (
  a
) => ({
  assessment_year:
    Number(
      a.assessmentYear
    ),

  adjustment_type_id:
    a.adjustmentTypeId,

  adjustment_date:
    a.adjustmentDate,

  description:
    a.description ||
    null,

  amount:
    Number(
      a.amount || 0
    ),

  notes:
    a.notes ||
    null,

  supporting_document_url:
    a.supportingDocumentUrl ||
    null,
});

export async function listTaxAdjustments(
  assessmentYear =
    null
) {
  let q =
    supabase
      .from(
        "tax_adjustments"
      )
      .select("*")
      .order(
        "adjustment_date",
        {
          ascending:
            false,
        }
      );

  if (
    assessmentYear !=
    null
  ) {
    q =
      q.eq(
        "assessment_year",
        Number(
          assessmentYear
        )
      );
  }

  const {
    data,
    error,
  } = await q;

  if (error) {
    throw error;
  }

  return (
    data || []
  ).map(
    taxAdjustmentFromRow
  );
}

export async function createTaxAdjustment(
  a
) {
  const {
    data:
      sess,
  } =
    await supabase.auth.getSession();

  const uid =
    sess?.session?.user?.id;

  if (!uid) {
    throw new Error(
      "Not authenticated"
    );
  }

  const {
    data,
    error,
  } =
    await supabase
      .from(
        "tax_adjustments"
      )
      .insert({
        ...taxAdjustmentToRow(
          a
        ),
        user_id:
          uid,
      })
      .select()
      .single();

  if (error) {
    throw error;
  }

  return taxAdjustmentFromRow(
    data
  );
}

export async function updateTaxAdjustment(
  id,
  a
) {
  const {
    data,
    error,
  } =
    await supabase
      .from(
        "tax_adjustments"
      )
      .update(
        taxAdjustmentToRow(
          a
        )
      )
      .eq(
        "id",
        id
      )
      .select()
      .single();

  if (error) {
    throw error;
  }

  return taxAdjustmentFromRow(
    data
  );
}
export async function deleteTaxAdjustment(
  id
) {
  const {
    error,
  } =
    await supabase
      .from(
        "tax_adjustments"
      )
      .delete()
      .eq(
        "id",
        id
      );

  if (error) {
    throw error;
  }

  return {
    success:
      true,
  };
}

// ============================================================
// Receipts / Attachments
// ============================================================

const attachmentFromRow = (
  r
) =>
  r && ({
    id:
      r.id,
    entityType:
      r.entity_type,
    entityId:
      r.entity_id,
    filePath:
      r.file_path,
    fileName:
      r.file_name,
    fileType:
      r.file_type,
    fileSize:
      r.file_size,
    uploadedAt:
      r.uploaded_at,
  });

export async function listAttachments(
  entityType,
  entityId
) {
  if (!entityId) {
    return [];
  }

  const {
    data,
    error,
  } =
    await supabase
      .from(
        "attachments"
      )
      .select("*")
      .eq(
        "entity_type",
        entityType
      )
      .eq(
        "entity_id",
        entityId
      )
      .order(
        "uploaded_at",
        {
          ascending:
            false,
        }
      );

  if (error) {
    throw error;
  }

  return (
    data || []
  ).map(
    attachmentFromRow
  );
}

export async function uploadAttachment({
  entityType,
  entityId,
  file,
  ext,
  mime,
}) {
  const {
    data:
      sess,
  } =
    await supabase.auth.getSession();

  const uid =
    sess?.session?.user?.id;

  if (!uid) {
    throw new Error(
      "Not authenticated"
    );
  }

  const uuid =
    (
      typeof crypto !==
        "undefined" &&
      crypto.randomUUID
    )
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()
          .toString(36)
          .slice(2)}`;

  const path =
    `${uid}/${entityType}/${entityId}/${uuid}.${ext}`;

  const {
    error:
      upErr,
  } =
    await supabase.storage
      .from(
        "receipts"
      )
      .upload(
        path,
        file,
        {
          contentType:
            mime,

          upsert:
            false,
        }
      );

  if (upErr) {
    throw upErr;
  }

  const {
    data,
    error,
  } =
    await supabase
      .from(
        "attachments"
      )
      .insert({
        user_id:
          uid,

        entity_type:
          entityType,

        entity_id:
          entityId,

        file_path:
          path,

        file_name:
          file.name ||
          `${uuid}.${ext}`,

        file_type:
          mime,

        file_size:
          file.size ||
          null,
      })
      .select()
      .single();

  if (error) {
    throw error;
  }

  return attachmentFromRow(
    data
  );
}

export async function getAttachmentSignedUrl(
  filePath,
  expiresIn =
    3600
) {
  const {
    data,
    error,
  } =
    await supabase.storage
      .from(
        "receipts"
      )
      .createSignedUrl(
        filePath,
        expiresIn
      );

  if (error) {
    throw error;
  }

  return data.signedUrl;
}

export async function deleteAttachment(
  attachment
) {
  await supabase.storage
    .from(
      "receipts"
    )
    .remove([
      attachment.filePath,
    ])
    .catch(
      () =>
        null
    );

  const {
    error,
  } =
    await supabase
      .from(
        "attachments"
      )
      .delete()
      .eq(
        "id",
        attachment.id
      );

  if (error) {
    throw error;
  }

  return {
    success:
      true,
  };
}

// ============================================================
// Events
// ============================================================

/*
 * ============================================================
 * EFFECTIVE EVENT RENTAL
 * ============================================================
 *
 * events.rental_cost stores ONLY the manual rental entered
 * directly inside Add / Edit Event.
 *
 * Prepaid Event Rental transactions stay in Transactions and
 * are linked to an exact Event through related_event_id.
 *
 * Whenever an Event is READ, we calculate:
 *
 * Manual Rental
 * + Linked Prepaid Event Rental
 * = Effective Rental
 *
 * The linked prepaid rental is also applied dynamically to:
 *
 * - Total Cost
 * - Net Profit
 * - Profit Margin
 * - Average Net Profit / Day
 *
 * IMPORTANT:
 *
 * We do NOT write the prepaid rental back into events.rental_cost.
 * This prevents double-counting.
 *
 * Example:
 *
 * Manual Rental = RM100
 * Linked Prepaid Rental = RM800
 *
 * Stored events.rental_cost = RM100
 * Displayed rentalCost = RM900
 *
 * If the prepaid transaction later changes from RM800 to RM850,
 * the Event automatically becomes RM950 on the next read.
 * ============================================================
 */

const applyLinkedPrepaidRentalToEvent = (
  event,
  linkedTransactions = []
) => {
  if (!event) {
    return event;
  }

  const manualRentalCost =
    Number(
      event.rentalCost ||
        0
    );

  const linkedPrepaidRentalCost =
    (
      linkedTransactions ||
      []
    ).reduce(
      (
        total,
        transaction
      ) =>
        total +
        Number(
          transaction.amount ||
            0
        ),
      0
    );

  const effectiveRentalCost =
    manualRentalCost +
    linkedPrepaidRentalCost;

  /*
   * The values saved inside the events table already contain
   * the MANUAL rental.
   *
   * Therefore we only add the LINKED prepaid portion here.
   */
  const baseTotalCost =
    Number(
      event.totalCost ||
        0
    );

  const baseNetProfit =
    Number(
      event.netProfit ||
        0
    );

  const effectiveTotalCost =
    baseTotalCost +
    linkedPrepaidRentalCost;

  const effectiveNetProfit =
    baseNetProfit -
    linkedPrepaidRentalCost;

  const totalRevenue =
    Number(
      event.totalSalesCollected ||
        0
    );

  const effectiveProfitMargin =
    totalRevenue >0
      ? (
          effectiveNetProfit /
          totalRevenue
        ) * 100
      : 0;

  const eventDays =
    Number(
      event.totalEventDays ||
        0
    );

  const effectiveAverageNetProfitPerDay =
    eventDays > 0
      ? effectiveNetProfit /
        eventDays
      : 0;

  return {
    ...event,

    /*
     * Keep the original manual rental available.
     *
     * AddEvent.js will use this field for the editable
     * Manual Rental Cost input.
     */
    manualRentalCost,

    /*
     * Linked prepaid rental information.
     */
    linkedPrepaidRentalCost,

    linkedPrepaidRentalTransactions:
      linkedTransactions ||
      [],

    /*
     * Effective figures used everywhere the event
     * is displayed/read.
     */
    rentalCost:
      effectiveRentalCost,

    totalCost:
      effectiveTotalCost,

    netProfit:
      effectiveNetProfit,

    profitMargin:
      effectiveProfitMargin,

    averageNetProfitPerDay:
      effectiveNetProfit /
      (
        eventDays > 0
          ? eventDays
          : 1
      ),
  };
};

/*
 * Fetch prepaid rental transactions for MULTIPLE events
 * in one query.
 *
 * This avoids doing one database request per event when
 * Event History or Monthly P&L loads many events.
 */
async function getLinkedPrepaidRentalMap(
  eventIds = []
) {
  const ids =
    (
      eventIds ||
      []
    ).filter(
      Boolean
    );

  if (
    ids.length ===
    0
  ) {
    return {};
  }

  /*
   * Find every Transaction Type using the
   * prepaid_event_rental treatment.
   */
  const {
    data:
      typeRows,
    error:
      typeError,
  } =
    await supabase
      .from(
        "transaction_types"
      )
      .select(
        "id"
      )
      .eq(
        "pl_treatment",
        "prepaid_event_rental"
      );

  if (typeError) {
    throw typeError;
  }

  const typeIds =
    (
      typeRows ||
      []
    )
      .map(
        (r) =>
          r.id
      )
      .filter(
        Boolean
      );

  if (
    typeIds.length ===
    0
  ) {
    return {};
  }

  /*
   * Fetch prepaid rental transactions linked to
   * any of the requested Event IDs.
   */
  const {
    data:
      txRows,
    error:
      txError,
  } =
    await supabase
      .from(
        "transactions"
      )
      .select("*")
      .in(
        "related_event_id",
        ids
      )
      .in(
        "transaction_type_id",
        typeIds
      )
      .order(
        "tx_date",
        {
          ascending:
            true,
        }
      );

  if (txError) {
    throw txError;
  }

  const map = {};

  (
    txRows ||
    []
  ).forEach(
    (row) => {
      const eventId =
        row.related_event_id;

      if (!eventId) {
        return;
      }

      if (
        !map[
          eventId
        ]
      ) {
        map[
          eventId
        ] = [];
      }

      map[
        eventId
      ].push(
        transactionFromRow(
          row
        )
      );
    }
  );

  return map;
};

/*
 * Event History sorting must happen AFTER prepaid rental
 * has been applied.
 *
 * Otherwise "Highest Profit", "Lowest Profit",
 * "Highest Margin", etc. would still sort using the
 * old stored Event totals.
 */
const sortEffectiveEvents = (
  events,
  sortBy
) => {
  const rows = [
    ...events,
  ];

  const numberValue = (
    value
  ) =>
    Number(
      value ||
        0
    );

  switch (
    sortBy
  ) {
    case "oldest":
      return rows.sort(
        (
          a,
          b
        ) =>
          String(
            a.startDate ||
              ""
          ).localeCompare(
            String(
              b.startDate ||
                ""
            )
          )
      );

    case "highest_sales":
      return rows.sort(
        (
          a,
          b
        ) =>
          numberValue(
            b.totalSalesCollected
          ) -
          numberValue(
            a.totalSalesCollected
          )
      );

    case "lowest_sales":
      return rows.sort(
        (
          a,
          b
        ) =>
          numberValue(
            a.totalSalesCollected
          ) -
          numberValue(
            b.totalSalesCollected
          )
      );

    case "highest_profit":
      return rows.sort(
        (
          a,
          b
        ) =>
          numberValue(
            b.netProfit
          ) -
          numberValue(
            a.netProfit
          )
      );

    case "lowest_profit":
      return rows.sort(
        (
          a,
          b
        ) =>
          numberValue(
            a.netProfit
          ) -
          numberValue(
            b.netProfit
          )
      );

    case "highest_margin":
      return rows.sort(
        (
          a,
          b
        ) =>
          numberValue(
            b.profitMargin
          ) -
          numberValue(
            a.profitMargin
          )
      );

    case "lowest_margin":
      return
        rows.sort(
          (
            a,
            b
          ) =>
            numberValue(
              a.profitMargin
            ) -
            numberValue(
              b.profitMargin
            )
        );

    case "latest":
    default:
      return rows.sort(
        (
          a,
          b
        ) =>
          String(
            b.startDate ||
              ""
          ).localeCompare(
            String(
              a.startDate ||
                ""
            )
          )
      );
  }
};

export async function listEventsSummary({
  month =
    "all",

  year =
    "all",

  sortBy =
    "latest",

  activityTypeId =
    "all",

  eventStatus =
    "all",

  plStatus =
    "all",

  search =
    "",
} = {}) {
  let query =
    supabase
      .from(
        "events"
      )
      .select("*");

  if (
    activityTypeId &&
    activityTypeId !==
      "all"
  ) {
    query =
      query.eq(
        "activity_type_id",
        activityTypeId
      );
  }

  if (
    eventStatus &&
    eventStatus !==
      "all"
  ) {
    query =
      query.eq(
        "status",
        eventStatus
      );
  }

  if (
    year !==
    "all"
  ) {
    const y =
      Number(
        year
      );

    if (
      month !==
      "all"
    ) {
      const m =
        Number(
          month
        );

      const start =
        `${y}-${String(
          m
        ).padStart(
          2,
          "0"
        )}-01`;

      const lastDay =
        new Date(
          y,
          m,
          0
        ).getDate();

      const end =
        `${y}-${String(
          m
        ).padStart(
          2,
          "0"
        )}-${String(
          lastDay
        ).padStart(
          2,
          "0"
        )}`;

      query =
        query
          .gte(
            "start_date",
            start
          )
          .lte(
            "start_date",
            end
          );
    } else {
      query =
        query
          .gte(
            "start_date",
            `${y}-01-01`
          )
          .lte(
            "start_date",
            `${y}-12-31`
          );
    }
  }

  const {
    data,
    error,
  } =
    await query;

  if (error) {
    throw error;
  }

  let rawRows =
    data ||
    [];

  /*
   * If Month is selected without a Year,
   * filter locally by month number.
   */
  if (
    year ===
      "all" &&
    month !==
      "all"
  ) {
    const m =
      Number(
        month
      );

    rawRows =
      rawRows.filter(
        (r) => {
          if (
            !r.start_date
          ) {
            return false;
          }

          return (
            Number(
              r.start_date.slice(
                5,
                7
              )
            ) ===
            m
          );
        }
      );
  }
  /*
   * Search filtering.
   */
  const term =
    (
      search ||
      ""
    )
      .trim()
      .toLowerCase();

  if (term) {
    rawRows =
      rawRows.filter(
        (r) => {
          const hay =
            [
              r.event_name,
              r.general_notes,
              r.other_cost_remark,
              r.other_revenue_remark,
              r.start_date,
            ]
              .filter(
                Boolean
              )
              .join(
                " "
              )
              .toLowerCase();

          return hay.includes(
            term
          );
        }
      );
  }

  /*
   * Load prepaid rental transactions for all Events
   * currently being displayed.
   */
  const eventIds =
    rawRows
      .map(
        (r) =>
          r.id
      )
      .filter(
        Boolean
      );

  const prepaidMap =
    await getLinkedPrepaidRentalMap(
      eventIds
    );

  /*
   * Convert Event rows and apply effective prepaid rental.
   */
  let events =
    rawRows.map(
      (r) => {
        const baseEvent =
          eventFromRow(
            r,
            [],
            []
          );

        return applyLinkedPrepaidRentalToEvent(
          baseEvent,
          prepaidMap[
            r.id
          ] ||
            []
        );
      }
    );

  if (
    plStatus &&
    plStatus !==
      "all"
  ) {
    events =
      events.filter(
        (
          event
        ) => {
          const np =
            Number(
              event.netProfit ||
                0
            );

          if (
            plStatus ===
            "profitable"
          ) {
            return np > 0;
          }

          if (
            plStatus ===
            "loss"
          ) {
            return np < 0;
          }

          if (
            plStatus ===
            "breakeven"
          ) {
            return (
              Math.abs(
                np
              ) <
              0.005
            );
          }

          return true;
        }
      );
  }

  return sortEffectiveEvents(
    events,
    sortBy
  );
}

export async function listEventYears() {
  const {
    data,
    error,
  } =
    await supabase
      .from(
        "events"
      )
      .select(
        "start_date"
      );

  if (error) {
    throw error;
  }

  const years =
    new Set();

  (
    data ||
    []
  ).forEach(
    (r) => {
      if (
        r.start_date
      ) {
        years.add(
          r.start_date.slice(
            0,
            4
          )
        );
      }
    }
  );

  return Array.from(
    years
  ).sort(
    (
      a,
      b
    ) =>
      b.localeCompare(
        a
      )
  );
}

export async function listEvents() {
  const {
    data:
      events,
    error,
  } =
    await supabase
      .from(
        "events"
      )
      .select("*")
      .order(
        "start_date",
        {
          ascending:
            false,
        }
      );

  if (error) {
    throw error;
  }

  const ids =
    (
      events ||
      []
    ).map(
      (e) =>
        e.id
    );

  if (
    ids.length ===
    0
  ) {
    return [];
  }

  const [
    {
      data:
        products,
      error:
        productsError,
    },

    {
      data:
        staff,
      error:
        staffError,
    },

    prepaidMap,
  ] =
    await Promise.all([
      supabase
        .from(
          "event_products"
        )
        .select("*")
        .in(
          "event_id",
          ids
        ),

      supabase
        .from(
          "event_staff"
        )
        .select("*")
        .in(
          "event_id",
          ids
        ),

      getLinkedPrepaidRentalMap(
        ids
      ),
    ]);

  if (
    productsError
  ) {
    throw productsError;
  }

  if (
    staffError
  ) {
    throw staffError;
  }

  const productsByEvent =
    {};

  (
    products ||
    []
  ).forEach(
    (p) => {
      productsByEvent[
        p.event_id
      ] =
        productsByEvent[
          p.event_id
        ] ||
        [];

      productsByEvent[
        p.event_id
      ].push(
        p
      );
    }
  );

  const staffByEvent =
    {};

  (
    staff ||
    []
  ).forEach(
    (s) => {
      staffByEvent[
        s.event_id
      ] =
        staffByEvent[
          s.event_id
        ] ||
        [];

      staffByEvent[
        s.event_id
      ].push(
        s
      );
    }
  );

  return (
    events ||
    []
  ).map(
    (e) => {
      const baseEvent =
        eventFromRow(
          e,
          productsByEvent[
            e.id
          ] ||
            [],
          staffByEvent[
            e.id
          ] ||
            []
        );

      return applyLinkedPrepaidRentalToEvent(
        baseEvent,
        prepaidMap[
          e.id
        ] ||
          []
      );
    }
  );
}

export async function getEvent(
  id
) {
  const [
    {
      data:
        e,
      error:
        e1,
    },

    {
      data:
        products,
      error:
        e2,
    },

    {
      data:
        staff,
      error:
        e3,
    },

    linkedPrepaidRentals,
  ] =
    await Promise.all([
      supabase
        .from(
          "events"
        )
        .select("*")
        .eq(
          "id",
          id
        )
        .maybeSingle(),

      supabase
        .from(
          "event_products"
        )
        .select("*")
        .eq(
          "event_id",
          id
        ),

      supabase
        .from(
          "event_staff"
        )
        .select("*")
        .eq(
          "event_id",
          id
        ),

      listLinkedPrepaidEventRentals(
        id
      ),
    ]);

  if (e1) {
    throw e1;
  }

  if (e2) {
    throw e2;
  }

  if (e3) {
    throw e3;
  }

  if (!e) {
    throw new Error(
      "Event not found"
    );
  }

  const baseEvent =
    eventFromRow(
      e,
      products ||
        [],
      staff ||
        []
    );

  return applyLinkedPrepaidRentalToEvent(
    baseEvent,
    linkedPrepaidRentals
  );
}

async function _validateEvent(
  e
) {
  if (
    !e.eventName ||
    !e.eventName.trim()
  ) {
    throw new Error(
      "Event name is required"
    );
  }

  if (
    !e.startDate
  ) {
    throw new Error(
      "Start date is required"
    );
  }

  if (
    !e.endDate
  ) {
    throw new Error(
      "End date is required"
    );
  }

  if (
    e.endDate <
    e.startDate
  ) {
    throw new Error(
      "End date cannot be earlier than start date"
    );
  }

  const revenueType =
    e.revenueType ||
    "product_sales";

  const validRevenueTypes =
    new Set([
      "product_sales",
      "other_revenue",
      "mixed",
    ]);

  if (
    !validRevenueTypes.has(
      revenueType
    )
  ) {
    throw new Error(
      "Invalid revenue type"
    );
  }

  if (
    Number(
      e.productSalesCollected ??
        e.totalSalesCollected ??
        0
    ) < 0
  ) {
    throw new Error(
      "Product sales collected cannot be negative"
    );
  }

  if (
    Number(
      e.otherRevenueAmount ??
        0
    ) < 0
  ) {
    throw new Error(
      "Other revenue amount cannot be negative"
    );
  }

  if (
    (
      e.rentalCost ||
      0
    ) < 0
  ) {
    throw new Error(
      "Rental cost cannot be negative"
    );
  }

  if (
    (
      e.transportCost ||
      0
    ) < 0
  ) {
    throw new Error(
      "Transport cost cannot be negative"
    );
  }

  if (
    (
      e.otherCost ||
      0
    ) < 0
  ) {
    throw new Error(
      "Other cost cannot be negative"
    );
  }

  const ids =
    (
      e.products ||
      []
    )
      .map(
        (p) =>
          p.productId
      )
      .filter(
        Boolean
      );

  const set =
    new Set(
      ids
    );

  if (
    set.size !==
    ids.length
  ) {
    throw new Error(
      "Duplicate products are not allowed in the same event"
    );
  }
}

export async function createEvent(
  e
) {
  await _validateEvent(
    e
  );

  const {
    data:
      created,
    error,
  } =
    await supabase
      .from(
        "events"
      )
      .insert(
        eventToRow(
          e
        )
      )
      .select()
      .single();

  if (error) {
    throw error;
  }

  const eventId =
    created.id;

  if (
    e.products &&
    e.products.length
  ) {
    const rows =
      e.products.map(
        (p) =>
          eventProductToRow(
            eventId,
            p
          )
      );

    const {
      error:
        err2,
    } =
      await supabase
        .from(
          "event_products"
        )
        .insert(
          rows
        );

    if (err2) {
      throw err2;
    }
  }

  if (
    e.staffWages &&
    e.staffWages.length
  ) {
    const rows =
      e.staffWages.map(
        (s) =>
          staffToRow(
            eventId,
            s
          )
      );

    const {
      error:
        err3,
    } =
      await supabase
        .from(
          "event_staff"
        )
        .insert(
          rows
        );

    if (err3) {
      throw err3;
    }
  }

  return await getEvent(
    eventId
  );
}

export async function updateEvent(
  id,
  e
) {
  await _validateEvent(
    e
  );

  const {
    error,
  } =
    await supabase
      .from(
        "events"
      )
      .update(
        eventToRow(
          e
        )
      )
      .eq(
        "id",
        id
      );

  if (error) {
    throw error;
  }

  await supabase
    .from(
      "event_products"
    )
    .delete()
    .eq(
      "event_id",
      id
    );

  await supabase
    .from(
      "event_staff"
    )
    .delete()
    .eq(
      "event_id",
      id
    );

  if (
    e.products &&
    e.products.length
  ) {
    const rows =
      e.products.map(
        (p) =>
          eventProductToRow(
            id,
            p
          )
      );

    const {
      error:
        err2,
    } =
      await supabase
        .from(
          "event_products"
        )
        .insert(
          rows
        );

    if (err2) {
      throw err2;
    }
  }

  if (
    e.staffWages &&
    e.staffWages.length
  ) {
    const rows =
      e.staffWages.map(
        (s) =>
          staffToRow(
            id,
            s
          )
      );

    const {
      error:
        err3,
    } =
      await supabase
        .from(
          "event_staff"
        )
        .insert(
          rows
        );

    if (err3) {
      throw err3;
    }
  }

  return await getEvent(
    id
  );
}

export async function deleteEvent(
  id
) {
  const {
    error,
  } =
    await supabase
      .from(
        "events"
      )
      .delete()
      .eq(
        "id",
        id
      );

  if (error) {
    throw error;
  }

  return {
    success:
      true,
  };
}
